import bluebird from 'bluebird'
import {
  CreateReducer,
  CreateAction,
} from '../utils/store'

import {
  NetworkThunk,
} from '../utils/network'

import {
  setHTTPToken,
  unsetHTTPToken,
  getHTTPTokenHeaders,
  handlers,
} from '../utils/api'

import {
  connectWebsocket,
} from '../utils/websocket'

import routerActions from './router'
import snackbarActions from './snackbar'
import projectActions from './project'

import {
  REFRESH_TOKEN_DELAY,
  ROUTE_LOGGED_IN,
  ROUTE_LOGGED_OUT,
} from '../../settings'

import {
  AuthState,
  ReducerHandler,
  ThunkHandler,
} from '../../types/store'

import {
  PayloadSetUser,
} from '../../types/auth'

import {
  isDevelopment,
} from '@kaidam/fc-shared/src/utils/settings'

import {
  IQuery_Id,
} from '@kaidam/fc-shared/src/types/utils'

import {
  IQuery_User_Login,
  IQuery_User_Register,
  IQuery_User_UpdatePassword,
  IData_UserToken,
  IQuery_User_ResetPassword,
} from '@kaidam/fc-shared/src/types/user'

import authSelectors from '../selectors/auth'

import LogRocket from 'logrocket'

import * as Sentry from '@sentry/react'

const prefix = 'auth'

/*

  initial state

*/
const initialState: AuthState = {
  loaded: false,
  token: '',
  data: null,
  loggingOut: false,
}

/*

  utils

*/
let refreshTokenLoop: any = null

/*

  reducers

*/
const setUser: ReducerHandler<AuthState, PayloadSetUser> = (state, action) => {
  state.loaded = true
  state.data = action.payload
}

const setToken: ReducerHandler<AuthState, string> = (state, action) => {
  const token = action.payload
  if(token) {
    setHTTPToken(token)
  }
  else {
    unsetHTTPToken()
  }
  localStorage.setItem('token', token)
  state.token = token
}

const setLoggingOut: ReducerHandler<AuthState, boolean> = (state, action) => {
  state.loggingOut = action.payload
}

/*

  thunks

*/
const authenticate: ThunkHandler<string, void> = (token) => async (dispatch, getState) => {

  // when impersonating - we might be running this again after the initial load
  await dispatch(actions.stopTokenLoop())

  let user: PayloadSetUser = null
  token = token || localStorage.getItem('token') || ''
  if(token) {
    try {
      user = await handlers.get<PayloadSetUser>('/auth/status', {
        headers: getHTTPTokenHeaders(token),
      })
    } catch(e) {

    }
  }
  if(user && user.id) {
    LogRocket.identify(user.id, {
      name: user.fullname,
      email: user.email
    })
    Sentry.setUser({
      id: user.id,
      email: user.email
    })
    dispatch(actions.setToken(token))
    try {
      await connectWebsocket(token)
    } catch(e) {
      dispatch(snackbarActions.setError(`error connecting to websocket: ${e.toString()}`))
      return
    }
    
    dispatch(startTokenLoop())
    dispatch(actions.setUser(user))
    await dispatch(projectActions.list())
  }
  else {
    // don't clear the token whilst we are reloading
    if(!isDevelopment()) {
      dispatch(actions.setToken(''))
    }
    dispatch(actions.setUser(null))
  }
}

const impersonateUser: ThunkHandler<string, void> = (id) => async (dispatch, getState) => {
  const isAdminUser = authSelectors.isAdminUser(getState())
  if(!isAdminUser) throw new Error(`you must be an admin user to impersonate another user`)
  const result = await handlers.post<IQuery_Id, IData_UserToken>('/user/impersonate', {
    id,
  })
  const impersonateReset = {
    token: getState().auth.token,
    route: getState().router.route,
  }
  localStorage.setItem('impersonate_existing_token', JSON.stringify(impersonateReset))
  await dispatch(authenticate(result.token))
  dispatch(routerActions.navigateTo('dashboard'))
  setTimeout(() => {
    window.location.reload()
  }, 1)
}

const unimpersonateUser: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  const resetRawData = localStorage.getItem('impersonate_existing_token')
  if(!resetRawData) throw new Error(`there was no data found`)
  const resetData = JSON.parse(resetRawData) as any
  localStorage.removeItem('impersonate_existing_token')
  const params = Object.assign({}, resetData.route.params, {
    access_token: resetData.token,
  })
  dispatch(routerActions.navigateTo(resetData.route.name, params))
  setTimeout(() => {
    window.location.reload()
  }, 100)
}

const refreshToken: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  try {
    const { token } = await handlers.post<void, IData_UserToken>('/auth/token')
    dispatch(actions.setToken(token))
  } catch(e) {
    // dispatch(actions.setToken(''))
    // dispatch(snackbarActions.setError(`you have been logged out`))
    // dispatch(logout())
  }
}

const startTokenLoop: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  if(refreshTokenLoop) {
    clearInterval(refreshTokenLoop)
  }
  refreshTokenLoop = setInterval(() => {
    dispatch(refreshToken())
  }, REFRESH_TOKEN_DELAY)
}

const stopTokenLoop: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  if(refreshTokenLoop) {
    clearInterval(refreshTokenLoop)
    refreshTokenLoop = null
  }
}

const login: ThunkHandler<IQuery_User_Login, void> = (payload) => async (dispatch, getState) => {
  const {
    token,
  } = await handlers.post<IQuery_User_Login, IData_UserToken>('/auth/login', payload)
  await dispatch(authenticate(token))
  dispatch(routerActions.navigateTo(ROUTE_LOGGED_IN))
}

const register: ThunkHandler<IQuery_User_Register, void> = (payload) => async (dispatch, getState) => {
  const {
    token,
  } = await handlers.post<IQuery_User_Register, IData_UserToken>('/auth/register', payload)
  await dispatch(authenticate(token))
  dispatch(routerActions.navigateTo(ROUTE_LOGGED_IN))
}

const resendRegisterEmail: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  await handlers.post<IQuery_User_Register, IData_UserToken>('/auth/resend_verify_email')
  dispatch(snackbarActions.setSuccess(`password reset request submitted - please check your email`))
}

const logout: ThunkHandler<void, void> = () => async (dispatch, getState) => {
  dispatch(actions.setToken(''))
  //dispatch(projectActions.setCurrentProjectId(''))
  dispatch(actions.setUser(null))
  dispatch(actions.setLoggingOut(true))
  localStorage.removeItem('impersonate_existing_token')
  document.location.pathname = '/'
  await bluebird.delay(10000)
}

const resetPassword: ThunkHandler<IQuery_User_ResetPassword, void> = (payload) => async (dispatch, getState) => {
  await handlers.post<IQuery_User_ResetPassword, void>('/auth/password_reset', payload)
  dispatch(snackbarActions.setSuccess(`password reset request submitted - please check your email`))
}

const updateSettings: ThunkHandler<object, void> = (payload) => async (dispatch, getState) => {
  const result = await handlers.put<object, PayloadSetUser>('/auth/settings', payload)
  dispatch(actions.setUser(result))
  dispatch(snackbarActions.setSuccess(`settings updated`))
}

const updatePassword: ThunkHandler<IQuery_User_UpdatePassword, void> = (payload) => async (dispatch, getState) => {
  const result = await handlers.put<IQuery_User_UpdatePassword, PayloadSetUser>('/auth/password', payload)
  dispatch(actions.setUser(result))
  dispatch(snackbarActions.setSuccess(`password updated`))
  dispatch(routerActions.navigateTo(ROUTE_LOGGED_IN))
}

/*

  collections

*/
const reducers = {
  setUser,
  setToken,
  setLoggingOut,
}

export const actions = {
  setUser: CreateAction(prefix ,'setUser', setUser),
  setToken: CreateAction(prefix ,'setToken', setToken),
  setLoggingOut: CreateAction(prefix ,'setLoggingOut', setLoggingOut),
  authenticate,
  refreshToken,
  startTokenLoop,
  stopTokenLoop,
  login: NetworkThunk(prefix, 'login', login, {
    snackbar: true,
    globalLoading: {
      message: 'logging in...',
    },
  }),
  register: NetworkThunk(prefix, 'register', register, {
    snackbar: true,
    globalLoading: {
      message: 'registering account...',
    },
  }),
  logout: NetworkThunk(prefix, 'logout', logout, {
    snackbar: true,
    globalLoading: {
      message: 'logging out...',
    },
  }),
  resendRegisterEmail: NetworkThunk(prefix, 'resendRegisterEmail', resendRegisterEmail, {
    snackbar: true,
    globalLoading: {
      message: 'sending email...',
    },
  }),
  resetPassword: NetworkThunk(prefix, 'resetPassword', resetPassword, {
    snackbar: true,
    globalLoading: {
      message: 'requesting password reset...',
    },
  }),
  impersonateUser: NetworkThunk(prefix, 'impersonateUser', impersonateUser, {
    snackbar: true,
    globalLoading: {
      message: 'impersonating user...',
    },
  }),
  unimpersonateUser: NetworkThunk(prefix, 'unimpersonateUser', unimpersonateUser, {
    snackbar: true,
    globalLoading: {
      message: 'unimpersonating user...',
    },
  }),
  updateSettings,
  updatePassword,
}

export const reducer = CreateReducer<AuthState>({
  prefix,
  initialState,
  reducers,
})

export default actions